import type { CSSProperties } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

const CATEGORIES = [
    { id: 'transport', label: 'Getting around', total: '$51.40', color: '#e8b65d' },
    { id: 'bills', label: 'Home and utilities', total: '$119.20', color: '#7fb8b0' },
    { id: 'fun', label: 'Fun and leisure', total: '$35.99', color: '#a3a9f2' }
]

const CHIPS = [
    { id: 'uber', label: 'Rideshare', amount: '$18.40', category: 0, slot: 0, scatter: { x: 50, y: 4, r: -7 } },
    { id: 'metro', label: 'Transit pass', amount: '$33.00', category: 0, slot: 1, scatter: { x: 3, y: 60, r: 5 } },
    { id: 'electric', label: 'Electricity', amount: '$64.20', category: 1, slot: 0, scatter: { x: 26, y: 31, r: -3 } },
    { id: 'internet', label: 'Broadband', amount: '$55.00', category: 1, slot: 1, scatter: { x: 52, y: 78, r: 6 } },
    { id: 'cinema', label: 'Movie night', amount: '$24.00', category: 2, slot: 0, scatter: { x: 1, y: 13, r: 4 } },
    { id: 'spotify', label: 'Streaming', amount: '$11.99', category: 2, slot: 1, scatter: { x: 48, y: 48, r: -5 } }
]

interface CategorizePreviewProps {
    active: boolean
}

/** Transaction chips that drift loosely, then snap into their categories. */
export const CategorizePreview = ({ active }: CategorizePreviewProps) => {
    const reduced = useReducedMotion()
    const [grouped, setGrouped] = useState(false)

    useEffect(() => {
        if (!active) return

        if (reduced) {
            setGrouped(true)
            return
        }

        let timer: ReturnType<typeof setTimeout>

        const cycle = (next: boolean) => {
            setGrouped(next)
            timer = setTimeout(() => cycle(!next), next ? 4800 : 2600)
        }

        timer = setTimeout(() => cycle(true), 1600)

        return () => clearTimeout(timer)
    }, [active, reduced])

    return (
        <div>
            <ul className='sr-only'>
                {CATEGORIES.map(category => (
                    <li key={category.id}>
                        {category.label}: {CHIPS.filter(chip => CATEGORIES[chip.category].id === category.id).map(chip => `${chip.label} ${chip.amount}`).join(', ')}
                    </li>
                ))}
            </ul>

            <div aria-hidden='true' className='glass-dark relative h-[270px] overflow-hidden rounded-[22px] p-4'>
                <div className='flex items-center justify-between text-[11px]'>
                    <span className='font-medium uppercase tracking-[0.2em] text-cream/55'>
                        Past seven days
                    </span>

                    <span className={cn('rounded-full px-2 py-0.5 transition-colors duration-500', grouped ? 'bg-citron/15 text-citron' : 'bg-white/5 text-cream/40')}>
                        {grouped ? 'All sorted' : 'Sorting now'}
                    </span>
                </div>

                <div className='relative mt-3 h-[212px]'>
                    {CATEGORIES.map((category, index) => (
                        <div
                            key={category.id}
                            className={cn('absolute left-0 right-0 flex items-center justify-between text-[11px] transition-all duration-700 ease-cinema', grouped ? 'opacity-100' : 'opacity-0')}
                            style={{ top: `${index * 34}%`, transitionDelay: grouped ? `${300 + index * 120}ms` : '0ms' } as CSSProperties}
                        >
                            <span className='flex items-center gap-1.5 text-cream/75'>
                                <span className='size-1.5 rounded-full' style={{ backgroundColor: category.color }} />
                                {category.label}
                            </span>

                            <span className='tabular-nums text-cream/45'>
                                {category.total}
                            </span>
                        </div>
                    ))}

                    {CHIPS.map((chip, index) => {
                        const category = CATEGORIES[chip.category]
                        const left = grouped ? chip.slot * 51 : chip.scatter.x
                        const top = grouped ? chip.category * 34 + 10 : chip.scatter.y
                        const rotate = grouped ? 0 : chip.scatter.r

                        return (
                            <div
                                key={chip.id}
                                className='absolute w-[49%] transition-[left,top,transform] duration-[1100ms] ease-[cubic-bezier(0.34,1.4,0.64,1)]'
                                style={{ left: `${left}%`, top: `${top}%`, transform: `rotate(${rotate}deg)`, transitionDelay: `${index * 45}ms` } as CSSProperties}
                            >
                                <div
                                    className={cn('glass flex items-center justify-between gap-2 rounded-full px-3 py-2 text-[11.5px] text-cream', !grouped && 'motion-drift')}
                                    style={{ animationDelay: `${index * -1.3}s`, animationDuration: `${8 + index}s` } as CSSProperties}
                                >
                                    <span className='flex min-w-0 items-center gap-1.5'>
                                        <span className='size-1.5 shrink-0 rounded-full' style={{ backgroundColor: category.color }} />
                                        <span className='truncate'>
                                            {chip.label}
                                        </span>
                                    </span>

                                    <span className='shrink-0 tabular-nums text-cream/60'>
                                        {chip.amount}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
